import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import {
  getContractWithVersions,
  getVersionDiff,
  type VersionRow,
  type VersionSummary,
  type ClauseDiff,
} from "@/lib/contracts.functions";
import { ClauseDiffView } from "@/components/ClauseDiffView";
import { toast } from "sonner";

export const Route = createFileRoute("/clause-history/$contractId")({
  head: () => ({
    meta: [
      { title: "Contract Versions — legaldecoder" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ContractVersionsPage,
});

function ContractVersionsPage() {
  const { contractId } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<{
    contract: { id: string; label: string };
    versions: VersionRow[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [versionA, setVersionA] = useState<string | null>(null);
  const [versionB, setVersionB] = useState<string | null>(null);
  const [diffData, setDiffData] = useState<{
    a: VersionSummary;
    b: VersionSummary;
    diff: ClauseDiff;
  } | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const fetchContract = useServerFn(getContractWithVersions);
  const fetchDiff = useServerFn(getVersionDiff);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate({ to: "/auth", search: { redirect: "/clause-history" } as never });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetchContract({ data: { contractId } })
      .then((res) => {
        if (!res) {
          toast.error("Contract not found.");
          navigate({ to: "/clause-history" });
          return;
        }
        setData(res);
        if (res.versions.length >= 2) {
          // Default: compare the two newest
          setVersionB(res.versions[0].id);
          setVersionA(res.versions[1].id);
        } else if (res.versions.length === 1) {
          setVersionB(res.versions[0].id);
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Couldn't load contract.");
      })
      .finally(() => setLoading(false));
  }, [user, contractId, fetchContract, navigate]);

  useEffect(() => {
    if (!versionA || !versionB || versionA === versionB) {
      setDiffData(null);
      return;
    }
    setDiffLoading(true);
    fetchDiff({ data: { versionAId: versionA, versionBId: versionB } })
      .then((res) => setDiffData(res))
      .catch((e) => {
        console.error(e);
        toast.error("Couldn't compute diff.");
      })
      .finally(() => setDiffLoading(false));
  }, [versionA, versionB, fetchDiff]);

  const versions = data?.versions ?? [];

  const versionOptions = useMemo(
    () =>
      versions.map((v) => ({
        id: v.id,
        label: `v${v.version_number} — ${new Date(v.created_at).toLocaleDateString()} · ${v.risk_level ?? "n/a"}`,
      })),
    [versions],
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/clause-history"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All contracts
        </Link>
        {loading || authLoading ? (
          <div className="flex items-center justify-center rounded-2xl border border-border bg-card py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight">{data.contract.label}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {versions.length} version{versions.length === 1 ? "" : "s"} tracked
            </p>

            {versions.length < 2 ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
                <p className="text-sm text-muted-foreground">
                  You need at least two versions to compare. Re-analyze this contract and save it as
                  a new version to start tracking changes.
                </p>
                <Button variant="hero" className="mt-6" asChild>
                  <Link to="/">Analyze new draft</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="mt-6 grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Compare from
                    <select
                      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={versionA ?? ""}
                      onChange={(e) => setVersionA(e.target.value)}
                    >
                      {versionOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Compare to
                    <select
                      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={versionB ?? ""}
                      onChange={(e) => setVersionB(e.target.value)}
                    >
                      {versionOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="mt-8">
                  {diffLoading ? (
                    <div className="flex items-center justify-center rounded-2xl border border-border bg-card py-16">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : diffData ? (
                    <ClauseDiffView a={diffData.a} b={diffData.b} diff={diffData.diff} />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Pick two different versions to see the diff.
                    </p>
                  )}
                </div>
              </>
            )}

            <p className="mt-12 text-center text-xs uppercase tracking-wider text-muted-foreground">
              Not legal advice. For informational purposes only.
            </p>
          </>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
